'use client';

import { FC, useEffect, useState } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { useCallOffOrderDetail, useCurrentCustomer, useOrderBaseUrls, useRequestDeliveryMutation } from '@ravago/shared/next-data-access/client';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { RequestDelivery } from '@ravago/shared/page-data/models/elements';
import { PermissionResources } from '@ravago/shared/page-data/models/features';
import { CallOffOrderDeliveryRequest } from '@ravago/shared/page-elements-radiance/components/content/call-off-order-details/components/call-off-order-delivery-request';
import { DynamicPageLink } from '@ravago/shared/page-elements-radiance/components/content/dynamic-page-link/dynamic-page-link';
import { SystemPageLink } from '@ravago/shared/page-elements-radiance/components/content/system-page-link/system-page-link';
import { CallOffProgressBar } from '@ravago/shared/page-elements-radiance/components/shared/order/call-off-progress-bar';
import { useCallOffAgreementContext } from '@ravago/shared/page-elements-radiance/context/call-off-agreement-context';
import { useFlyoutContext } from '@ravago/shared/page-elements-radiance/hooks/use-flyout-context';
import { usePermission } from '@ravago/shared/page-elements-radiance/hooks/use-permission';
import { DynamicProductBlockUtils } from '@ravago/shared/page-elements-radiance/utils/dynamic-product-block/dynamic-product-block.utils';
import { getLanguageCode } from '@ravago/shared/page-elements-radiance/utils/locale/locale.utils';
import { TrackingUtils } from '@ravago/shared/page-elements-radiance/utils/tracking/tracking.utils';
import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Flyout, FlyoutActions, FlyoutContent, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Image } from '@ravago/shared/radiance/components/image/components/Image/Image';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import CallOffContactInfo from './components/call-off-contact-info';
import CallOffDeliveryInfo from './components/call-off-delivery-info';
import CallOffProductInfo from './components/call-off-product-info';

interface Props {
  locale: string;
}

const CallOffAgreementFlyout: FC<Props> = ({ locale }) => {
  const translate = useTranslations();
  const language = getLanguageCode(locale);
  const { phone } = useBreakpoint();
  const { user, accessToken } = useAuthentication();
  const { state: flyoutState, dispatch: flyoutDispatch } = useFlyoutContext();
  const { state: callOffAgreementState, dispatch: callOffAgreementDispatch } = useCallOffAgreementContext();
  const [currentStep, setCurrentStep] = useState(1);
  const { deliveryDate, callOffId, quantity, additionalInfo, openedFrom, usesAgreementApp = false } = callOffAgreementState;
  const { hasPermission: canViewPrices, isLoading: permissionLoading } = usePermission(PermissionResources.ORDER_PRICE);

  const { orderBaseUrls } = useOrderBaseUrls({ language });
  const { callOffOrderDetail, isLoading: isLoadingCallOffDetail } = useCallOffOrderDetail({
    callOffId,
    locale,
    enabled: flyoutState.idOpenDialog === 'call-off-agreement' && !callOffAgreementState.callOffOrderDetail && !!callOffId
  });
  const { customer, isLoading: isLoadingCustomer } = useCurrentCustomer({
    enabled: flyoutState.idOpenDialog === 'call-off-agreement'
  });
  const requestDeliveryMutation = useRequestDeliveryMutation();

  const activeCallOffOrderDetail = callOffAgreementState.callOffOrderDetail ?? callOffOrderDetail;

  useEffect(() => {
    if (callOffAgreementState.callOffOrderDetail) {
      setCurrentStep(2);
    } else if (callOffId && flyoutState.idOpenDialog === 'call-off-agreement') {
      setCurrentStep(1);
    }
  }, [callOffAgreementState.callOffOrderDetail, callOffId, flyoutState.idOpenDialog]);

  const resetValues = () => {
    callOffAgreementDispatch?.({ type: 'clear_call_off', value: {} });
    setCurrentStep(1);
  };

  const updateStateForDynamicProductBlock = () => {
    callOffAgreementDispatch?.({
      type: 'set_call_off_additional_info',
      value: { additionalInfo: additionalInfo || '' }
    });
  };

  const handleFlyoutClose = () => {
    if (openedFrom === 'dynamic-product-block' && quantity && currentStep !== 3) {
      updateStateForDynamicProductBlock();
    } else if (currentStep === 3 || callOffId) {
      resetValues();
    }
    flyoutDispatch?.({ type: 'close_flyout', value: { flyoutType: 'call-off-agreement', title: undefined } });
  };

  const handleDeliveryRequest = async () => {
    if (activeCallOffOrderDetail && quantity && deliveryDate && accessToken) {
      const request: RequestDelivery = {
        callOffOrderId: activeCallOffOrderDetail.id,
        quantity,
        productReference: activeCallOffOrderDetail.customerProductReferences[0],
        orderReference: activeCallOffOrderDetail.customerOrderReferences[0],
        deliveryDate,
        info: additionalInfo || '',
        packaging: activeCallOffOrderDetail.packagings[0]
      };

      requestDeliveryMutation.mutate(request, {
        onSuccess: () => {
          TrackingUtils.track('click_request_delivery_button', {
            callOffId: activeCallOffOrderDetail.id,
            productId: activeCallOffOrderDetail.product.id.toString(),
            productName: activeCallOffOrderDetail.product.description
          });
          setCurrentStep(3);
        },
        onError: () => {
          setCurrentStep(3);
        }
      });
    }
  };

  const handleTryAgain = () => {
    requestDeliveryMutation.reset();
    setCurrentStep(2);
    handleFlyoutClose();
  };

  return (
    <Flyout open={flyoutState.idOpenDialog === 'call-off-agreement'} closeOnOutsideClick onFlyoutClose={handleFlyoutClose}>
      <FlyoutHeader> {currentStep === 1 ? `${translate('page-consumer.call-off-agreement-flyout.call-off-agreement')} ${activeCallOffOrderDetail?.id ?? ''}` : translate('page-consumer.call-off-agreement-flyout.request-delivery')}</FlyoutHeader>

      <FlyoutContent>
        {activeCallOffOrderDetail && !permissionLoading ? (
          <>
            {currentStep === 1 && (
              <Box direction="column">
                {usesAgreementApp && <CallOffProgressBar callOffOrderDetail={activeCallOffOrderDetail} showLabel showSupportingText />}
                <Box direction="column" gap="md">
                  <Typography component="span" variant="body-highlight">
                    {translate('page-consumer.call-off-agreement-flyout.request-delivery')}
                  </Typography>
                  <CallOffOrderDeliveryRequest callOffOrderDetail={activeCallOffOrderDetail} showDeliveryAddress={false} usesAgreementApp={usesAgreementApp} onClick={() => setCurrentStep(2)} />
                </Box>
                <Box direction="column" gap="md">
                  <Box items="center">
                    <Avatar size="large" shape="square" src={activeCallOffOrderDetail.contentfulProduct?.brand?.producer?.logo?.url} />
                    <Box direction="column" gap="none">
                      {activeCallOffOrderDetail.contentfulProduct ? (
                        <DynamicPageLink
                          id={`dynamic-link__call-off-agreement__step-1__${activeCallOffOrderDetail.contentfulProduct.slug}`}
                          locale={locale}
                          baseRoute={{
                            url: orderBaseUrls.productBaseUrl ?? undefined,
                            noFollow: false
                          }}
                          slug={DynamicProductBlockUtils.getProductSlugWithParams(activeCallOffOrderDetail.contentfulProduct.slug, activeCallOffOrderDetail.product.color, activeCallOffOrderDetail.product.producerColor, 'call-off')}
                        >
                          <Typography component="span" variant="body-highlight" color="primary">
                            {activeCallOffOrderDetail.product.description}
                          </Typography>
                        </DynamicPageLink>
                      ) : (
                        <Typography component="span" variant="body-highlight" color="primary">
                          {activeCallOffOrderDetail.product.description}
                        </Typography>
                      )}
                      {activeCallOffOrderDetail.customerProductReferences?.length > 0 && (
                        <Typography component="span" variant="body">
                          {activeCallOffOrderDetail.customerProductReferences.join(' • ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <CallOffProductInfo callOffOrderDetail={activeCallOffOrderDetail} usesAgreementApp={usesAgreementApp} showTotalQuantity canViewPrices={canViewPrices} />
                <Box gap="sm" width="100%">
                  <SystemPageLink baseRoute={orderBaseUrls.callOffAgreementBaseUrl} locale={locale} type={SystemPageType.CALL_OFF_ORDER_DETAIL} slug={activeCallOffOrderDetail.id}>
                    <Button fullWidth variant="primary">
                      {translate('page-consumer.call-off-agreement-flyout.view-call-off')}
                    </Button>
                  </SystemPageLink>
                  <SystemPageLink baseRoute={orderBaseUrls.myProductsAndPricesBaseUrl} locale={locale} type={SystemPageType.MY_PRODUCTS_AND_PRICES}>
                    <Button fullWidth variant="secondary">
                      {translate('page-consumer.order-line-flyout.new-quote')}
                    </Button>
                  </SystemPageLink>
                </Box>
              </Box>
            )}
            {currentStep === 2 && (
              <Box direction="column">
                <Box direction="column" gap="none">
                  <Box items="center">
                    <Avatar size="large" shape="square" src={activeCallOffOrderDetail.contentfulProduct?.brand?.producer?.logo?.url} />
                    <Box direction="column" gap="none">
                      {activeCallOffOrderDetail.contentfulProduct ? (
                        <DynamicPageLink
                          id={`dynamic-link__call-off-agreement__step-2__${activeCallOffOrderDetail.contentfulProduct.slug}`}
                          locale={locale}
                          baseRoute={{
                            url: orderBaseUrls.productBaseUrl ?? undefined,
                            noFollow: false
                          }}
                          slug={activeCallOffOrderDetail.contentfulProduct.slug}
                        >
                          <Typography component="span" variant="body-highlight" color="primary">
                            {activeCallOffOrderDetail.product.description}
                          </Typography>
                        </DynamicPageLink>
                      ) : (
                        <Typography component="span" variant="body-highlight" color="primary">
                          {activeCallOffOrderDetail.product.description}
                        </Typography>
                      )}
                      {activeCallOffOrderDetail.customerProductReferences && (
                        <Typography component="span" variant="body">
                          {activeCallOffOrderDetail.customerProductReferences.join(' • ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box direction="column" gap="xl">
                  <Box direction="column" gap="sm">
                    <Typography component="span" variant="body-highlight">
                      {translate('page-consumer.call-off-agreement-flyout.delivery-information')}
                    </Typography>
                    {deliveryDate && quantity && <CallOffDeliveryInfo deliveryAddress={activeCallOffOrderDetail.destinationAddress} deliveryDate={deliveryDate} quantity={quantity} additionalInfo={additionalInfo || '-'} />}
                  </Box>
                  <Box direction="column" gap="sm">
                    <Typography component="span" variant="body-highlight">
                      {translate('page-consumer.call-off-agreement-flyout.product-information')}
                    </Typography>
                    <CallOffProductInfo callOffOrderDetail={activeCallOffOrderDetail} usesAgreementApp={usesAgreementApp} canViewPrices={canViewPrices} />
                  </Box>
                  <Box direction="column" gap="sm">
                    <Typography component="span" variant="body-highlight">
                      {translate('page-consumer.call-off-agreement-flyout.contact-information')}
                    </Typography>
                    {user && <CallOffContactInfo translate={translate} companyName={customer?.name} email={user.mail} firstName={user.firstname} lastName={user.lastname} country={activeCallOffOrderDetail.destinationAddress?.countryName} />}
                  </Box>
                </Box>
              </Box>
            )}
            {currentStep === 3 && (
              <Box direction="column">
                {requestDeliveryMutation.isError ? (
                  <Box height="60vh" width="auto" direction="column" justify="center" items="center" gap="xl" spacing={{ left: '3xl', right: '3xl' }}>
                    <Image src="/images/rfq-error.png" alt={translate('page-consumer.request-for-quotation.error')} title={translate('page-consumer.request-for-quotation.error')} objectFit="contain" />
                    <Typography align="center" component="h4" variant="heading-4" color="primary">
                      {translate('page-consumer.call-off-agreement-flyout.error')}
                    </Typography>
                    <Typography align="center" component="b" variant="body" color="strong">
                      {translate('page-consumer.call-off-agreement-flyout.error-message')}
                    </Typography>
                  </Box>
                ) : (
                  <Box height="60vh" width="auto" direction="column" justify="center" items="center" gap="xl" spacing={{ left: '3xl', right: '3xl' }}>
                    <Image src="/images/rfq-thank-you.png" alt={translate('page-consumer.request-for-quotation.thank-you')} title={translate('page-consumer.request-for-quotation.thank-you')} objectFit="contain" />
                    <Typography align="center" component="h4" variant="heading-4" color="primary">
                      {translate('page-consumer.call-off-agreement-flyout.thank-you')}
                    </Typography>
                    <Typography align="center" component="b" variant="body" color="strong">
                      {translate('page-consumer.call-off-agreement-flyout.thank-you-message')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        ) : (
          (isLoadingCallOffDetail || permissionLoading || isLoadingCustomer) && (
            <Box direction="column" items="center">
              <Spinner />
            </Box>
          )
        )}
      </FlyoutContent>

      <FlyoutActions>
        {currentStep === 2 && (
          <>
            <Button variant="secondary" onClick={() => (callOffAgreementState.callOffId ? setCurrentStep(1) : handleFlyoutClose())} fullWidth leadingIcon={!phone ? 'long-arrow-left' : ''}>
              {translate('page-consumer.call-off-agreement-flyout.back')}
            </Button>
            <Button variant="primary" onClick={handleDeliveryRequest} fullWidth trailingIcon={!phone ? 'long-arrow-right' : ''} disabled={user?.groups?.includes(UserGroups.ravagoGlobalViewer) || requestDeliveryMutation.isPending}>
              {translate('page-consumer.call-off-agreement-flyout.send-request')}
            </Button>
          </>
        )}
        {currentStep === 3 && (
          <>
            {requestDeliveryMutation.isError && (
              <Button variant="primary" fullWidth onClick={handleTryAgain} trailingIcon={!phone ? 'refresh' : ''}>
                {translate('page-consumer.call-off-agreement-flyout.try-again')}
              </Button>
            )}
            <Button variant="secondary" fullWidth onClick={handleFlyoutClose}>
              {translate('page-consumer.call-off-agreement-flyout.close')}
            </Button>
          </>
        )}
      </FlyoutActions>
    </Flyout>
  );
};

export default CallOffAgreementFlyout;
